import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactSelect, { components } from "react-select";
import { AiOutlineCheck } from "react-icons/ai";
import { BiCloudDownload } from "react-icons/bi";
import { 
  getScrappedValidatedByResource, 
  getDistinctUsersReport,
  getValidatedByResourceTyreData,
  downloadValidatedByResourceTyres
} from "../../apis/scrappedCasingTyres.service";
import { exportScrapCasingTyresExcel } from "../../utils/exportExcel";
import toast from "react-hot-toast";
import Spinner from "../Spinner";
import ValidatedTyresModal from "./ValidatedTyresModal";
import CustomDetailModal from "./CustomDetailModal";
import dayjs from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  VALIDATED_TYRES_RESOURCE_DETAIL_COLUMNS,
  isScrapTyreStatus,
  resolveCasingTypeDisplay,
} from "../../utils/scrapConstants";

const styles = {
  container: "w-full max-w-[1400px]",
  card: "w-full bg-white rounded-3xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-4 md:p-8 relative",
  header: "flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4",
  title: "text-3xl font-bold text-green-500 w-full md:w-auto text-left",
  filterWrapper: "flex flex-wrap items-center gap-4 w-full md:w-auto justify-end",
  dateGroup: "flex flex-wrap items-center gap-4",
  dateInputWrapper: "relative flex items-center border border-[#858585] rounded-md bg-white hover:bg-gray-50 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 shadow-sm transition-colors w-full md:w-auto h-full px-2",
  dateLabel: "text-xs text-[#2C2C2C] opacity-60 mr-1",
  dateInput: "appearance-none outline-none border-none bg-transparent pr-2 py-1.5 text-xs font-medium text-[#2C2C2C] w-full md:w-auto cursor-pointer",
  tableWrapper: "w-full overflow-x-auto overflow-y-hidden min-h-[300px]",
  table: "w-full text-left border-collapse min-w-max",
  tableHead: "sticky top-0 bg-white z-10 border-y border-gray-300",
  th: "py-5 px-6 text-[15px] font-bold text-gray-800 whitespace-nowrap text-left",
  thCenter: "py-5 px-6 text-[15px] font-bold text-gray-800 text-center whitespace-nowrap",
  tr: "border-b border-gray-200 last:border-0 hover:bg-gray-50 transition-colors cursor-pointer",
  td: "py-5 px-6 text-[14px] font-medium text-gray-700 whitespace-nowrap text-left",
  tdCenter: "py-5 px-6 text-[14px] text-gray-500 text-center whitespace-nowrap",
  tdBoldCenter: "py-5 px-6 text-[14px] text-gray-700 font-bold text-center whitespace-nowrap",
  noData: "py-10 text-center text-gray-500 font-medium",
  totalRow: "bg-gray-50 font-bold sticky bottom-0 z-10",
  totalTd: "py-5 px-6 text-gray-800 whitespace-nowrap text-left font-bold bg-gray-50",
  totalTdCenter: "py-5 px-6 text-gray-700 text-center whitespace-nowrap font-bold bg-gray-50",
  totalTdBoldCenter: "py-5 px-6 text-gray-800 text-center whitespace-nowrap border-none font-bold bg-gray-50",
  optionRow: "flex items-center justify-between gap-2 overflow-hidden",
  optionLabel: "truncate",
  checkIcon: "text-green-600 flex-shrink-0",
  valueContainer: {
    maxWidth: "220px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
  },
};

const CustomOption = (props) => (
  <components.Option {...props}>
    <div className={styles.optionRow}>
      <span className={styles.optionLabel}>{props.label}</span>
      {props.isSelected && <AiOutlineCheck className={styles.checkIcon} />}
    </div>
  </components.Option>
);

const CustomValueContainer = ({ children, ...props }) => {
  const { getValue, hasValue } = props;
  const nbValues = getValue().length;
  if (!hasValue || nbValues <= 1) {
    return (
      <components.ValueContainer {...props}>
        <div style={styles.valueContainer}>{children}</div>
      </components.ValueContainer>
    );
  }
  return (
    <components.ValueContainer {...props}>
      <div style={styles.valueContainer}>
        {Array.isArray(children[0]) ? children[0][0] : children[0]}
        <span className="text-xs font-semibold text-gray-500 ml-2 whitespace-nowrap">
          +{nbValues - 1} more
        </span>
        {children[1]}
      </div>
    </components.ValueContainer>
  );
};
const ValidatedTyresResourceTable = ({ rowsPerPage = 5 }) => {
  const defaultDateFrom = new Date();
  defaultDateFrom.setMonth(defaultDateFrom.getMonth() - 3);
  const todayDate = dayjs();
  const [dateFrom, setDateFrom] = useState(defaultDateFrom.toISOString().split('T')[0]);
  const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);

  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [resourceOptions, setResourceOptions] = useState([]);
  const [selectedResourceFilters, setSelectedResourceFilters] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null); 
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleDateFromChange = (value) => {
    if (!value) {
      setDateFrom("");
      return;
    }
    if (!value.isValid()) return;
    if (value.isAfter(todayDate, "day")) return;
    if (dateTo && value.isAfter(dayjs(dateTo), "day")) return;
    setDateFrom(value.format("YYYY-MM-DD"));
  };

  const handleDateToChange = (value) => {
    if (!value) {
      setDateTo("");
      return;
    }
    if (!value.isValid()) return;
    if (value.isAfter(todayDate, "day")) return;
    if (dateFrom && value.isBefore(dayjs(dateFrom), "day")) return;
    setDateTo(value.format("YYYY-MM-DD"));
  };

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const response = await getDistinctUsersReport({ 
          dateFrom, 
          dateTo 
        });
        if (response?.data) {
          const options = response.data.map((user) => ({
            value: user._id,
            label: user.name,
          }));
          setResourceOptions(options);
        }
      } catch (error) {
        console.error("Error fetching resources:", error);
      }
    };
    if (dateFrom && dateTo) fetchResources();
  }, [dateFrom, dateTo]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const payload = {
          dateFrom,
          dateTo,
        };
        if (selectedResourceFilters?.length === 1) {
          payload.resourceId = selectedResourceFilters[0].value;
        } else if (selectedResourceFilters?.length > 1) {
          payload.resourceIds = selectedResourceFilters.map((r) => r.value).filter(Boolean);
        }
        const response = await getScrappedValidatedByResource(payload);
        
        const rows = response?.data || [];
        setAllRows(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error("Error fetching table data:", error);
      } finally {
        setLoading(false);
      }
    };
    if (dateFrom && dateTo) fetchData();
  }, [dateFrom, dateTo, selectedResourceFilters, rowsPerPage]);

  const totals = useMemo(() => {
    return allRows.reduce((acc, row) => {
      acc["Casing A"] += (row["Casing A"] || 0);
      acc["Casing B"] += (row["Casing B"] || 0);
      acc["Casing C"] += (row["Casing C"] || 0);
      acc["Casing D"] += (row["Casing D"] || 0);
      acc.scrap += (row.scrap || 0);
      acc.total += (row.total || 0);
      return acc;
    }, { "Casing A": 0, "Casing B": 0, "Casing C": 0, "Casing D": 0, scrap: 0, total: 0 });
  }, [allRows]);


  const reactSelectCustomStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "36px",
      minWidth: "200px",
      borderRadius: "0.375rem",
      borderColor: "#858585",
      fontSize: "0.875rem",
      fontWeight: "500",
      color: "#2C2C2C",
      "&:hover": {
        borderColor: "#6B7280",
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0 8px",
      flexWrap: "nowrap",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#2C2C2C",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#22C55E" : state.isFocused ? "#F3F4F6" : "white",
      color: state.isSelected ? "white" : "#2C2C2C",
    }),
    dropdownIndicator: (provided) => ({ ...provided, padding: "0 8px" }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (provided) => ({
      ...provided,
      zIndex: 30,
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: "220px",
    }),
  };

  useEffect(() => {
    if (!selectedDetail) {
      setModalData([]);
      return;
    }

    const fetchDetailData = async () => {
      setModalLoading(true);
      try {
        const payload = {
          dateFrom,
          dateTo,
        };

        const normalizedType = (selectedDetail.type || "").trim().toLowerCase();
        const shouldSendTyreStatus =
          normalizedType &&
          normalizedType !== "total" &&
          normalizedType !== "grand total";

        if (shouldSendTyreStatus) payload.tyreStatus = selectedDetail.type;

        if (selectedDetail.resourceId) payload.resourceId = selectedDetail.resourceId;
        if (Array.isArray(selectedDetail.resourceIds) && selectedDetail.resourceIds.length > 0) {
          payload.resourceIds = selectedDetail.resourceIds;
        }

        const response = await getValidatedByResourceTyreData(payload);
        const rows = response?.data || response || [];
        setModalData(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error("Error fetching validated by resource tyre data:", error);
        setModalData([]);
      } finally {
        setModalLoading(false);
      }
    };

    fetchDetailData();
  }, [selectedDetail, dateFrom, dateTo]);

  const resolveDownloadTyreRows = (response) => {
    if (!response) return [];
    if (Array.isArray(response)) return response;
    if (Array.isArray(response.data)) return response.data;
    return [];
  };

  const buildValidatedByResourceDownloadPayload = () => {
    const payload = { dateFrom, dateTo };
    if (!selectedDetail) return null;

    const normalizedType = (selectedDetail.type || "").trim().toLowerCase();
    const shouldSendTyreStatus =
      normalizedType &&
      normalizedType !== "total" &&
      normalizedType !== "grand total";
    if (shouldSendTyreStatus) payload.tyreStatus = selectedDetail.type;

    if (selectedDetail.resourceId) payload.resourceId = selectedDetail.resourceId;
    if (Array.isArray(selectedDetail.resourceIds) && selectedDetail.resourceIds.length > 0) {
      payload.resourceIds = selectedDetail.resourceIds;
    }
    return payload;
  };

  const handleDownloadDetailData = async () => {
    const payload = buildValidatedByResourceDownloadPayload();
    if (!payload) return;
    if (!payload.resourceId && (!payload.resourceIds || !payload.resourceIds.length)) {
      const allResourceIds = allRows.map((r) => r._id).filter(Boolean);
      if (allResourceIds.length) {
        payload.resourceIds = allResourceIds;
      }
    }
    setDownloadLoading(true);
    try {
      const response = await downloadValidatedByResourceTyres(payload);
      const list = resolveDownloadTyreRows(response);
      if (!list.length) {
        toast.error("No data to download");
        return;
      }
      const safeTitle = `${selectedDetail?.resource || "tyres"}-${selectedDetail?.type || "data"}`
        .replace(/[/\\?%*:|"<>]/g, "-")
        .replace(/\s+/g, "_");
      exportScrapCasingTyresExcel(list, `validated-by-resource_${safeTitle}`, {
        tyreStatus: selectedDetail?.type,
      });
      toast.success("File downloaded");
    } catch (error) {
      console.error("Download validated by resource tyres failed:", error);
    } finally {
      setDownloadLoading(false);
    }
  };

  const isScrapDetail = isScrapTyreStatus(selectedDetail?.type);

  return (
    <div className={styles.container}>
      <div className={styles.card}>

        <div className={styles.header}>
          <h2 className={styles.title}>
            Validated Tyres by Resource
          </h2>
          
          <div className={styles.filterWrapper}>
             <div className={styles.dateGroup}>
              <div className={styles.dateInputWrapper}>
                <span className={styles.dateLabel}>From:</span>
                <DatePicker
                  value={dateFrom ? dayjs(dateFrom) : null}
                  onChange={handleDateFromChange}
                  format="DD/MM/YYYY"
                  maxDate={dateTo ? dayjs(dateTo) : todayDate}
                  disableFuture
                  slotProps={{
                    actionBar: { actions: ["clear", "today"] },
                    textField: {
                      variant: "standard",
                      sx: {
                        width: 100,
                        "& input": { fontSize: 12, padding: "9px 0" },
                      },
                      InputProps: { disableUnderline: true },
                    },
                  }}
                />
              </div>
              <div className={styles.dateInputWrapper}>
                <span className={styles.dateLabel}>To:</span>
                <DatePicker
                  value={dateTo ? dayjs(dateTo) : null}
                  onChange={handleDateToChange}
                  format="DD/MM/YYYY"
                  minDate={dateFrom ? dayjs(dateFrom) : undefined}
                  maxDate={todayDate}
                  disableFuture
                  slotProps={{
                    actionBar: { actions: ["clear", "today"] },
                    textField: {
                      variant: "standard",
                      sx: {
                        width: 100,
                        "& input": { fontSize: 12, padding: "9px 0" },
                      },
                      InputProps: { disableUnderline: true },
                    },
                  }}
                />
              </div>
            </div>
            
            <div className="w-full md:w-auto min-w-[200px]">
              <ReactSelect
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                options={resourceOptions}
                value={selectedResourceFilters}
                onChange={(opts) => {
                  setSelectedResourceFilters(opts || []);
                }}
                components={{ Option: CustomOption, ValueContainer: CustomValueContainer }}
                placeholder="Select Resource(s)"
                styles={reactSelectCustomStyles}
                isSearchable
              />
            </div>
          </div>
        </div>

        <div  className="w-full overflow-x-auto overflow-y-auto min-h-[300px] max-h-[520px]">
          {loading ? (
            <div className="flex justify-center items-center h-[300px]">
              <Spinner />
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr className={styles.tableHead}>
                  <th className={styles.th}>Validated By</th>
                  <th className={styles.thCenter}>Casing A</th>
                  <th className={styles.thCenter}>Casing B</th>
                  <th className={styles.thCenter}>Casing C</th>
                  <th className={styles.thCenter}>Casing D</th>
                  <th className={styles.thCenter}>Scrap</th>
                  <th className={styles.thCenter}>Total</th>
                </tr>
              </thead>
              <tbody>
                {allRows.length > 0 ? (
                  allRows.map((row, index) => (
                    <tr 
                      key={row._id || index} 
                      className={styles.tr}
                    >
                      <td 
                        className={`${styles.td} text-blue-600 cursor-pointer hover:underline`}
                        onClick={() => setSelectedResource({ id: row._id, name: row.validatedBy })}
                      >
                        {row.validatedBy}
                      </td>
                      <td 
                        className={`${styles.tdCenter} text-blue-600 cursor-pointer hover:underline`}
                        onClick={() => setSelectedDetail({ resource: row.validatedBy, resourceId: row._id, type: "Casing A", value: row["Casing A"] })}
                      >
                        {row["Casing A"] ?? 0}
                      </td>
                      <td 
                        className={`${styles.tdCenter} text-blue-600 cursor-pointer hover:underline`}
                        onClick={() => setSelectedDetail({ resource: row.validatedBy, resourceId: row._id, type: "Casing B", value: row["Casing B"] })}
                      >
                        {row["Casing B"] ?? 0}
                      </td>
                      <td 
                        className={`${styles.tdCenter} text-blue-600 cursor-pointer hover:underline`}
                        onClick={() => setSelectedDetail({ resource: row.validatedBy, resourceId: row._id, type: "Casing C", value: row["Casing C"] })}
                      >
                        {row["Casing C"] ?? 0}
                      </td>
                      <td 
                        className={`${styles.tdCenter} text-blue-600 cursor-pointer hover:underline`}
                        onClick={() => setSelectedDetail({ resource: row.validatedBy, resourceId: row._id, type: "Casing D", value: row["Casing D"] })}
                      >
                        {row["Casing D"] ?? 0}
                      </td>
                      <td 
                        className={`${styles.tdCenter} text-blue-600 cursor-pointer hover:underline`}
                        onClick={() => setSelectedDetail({ resource: row.validatedBy, resourceId: row._id, type: "Scrap", value: row.scrap })}
                      >
                        {row.scrap ?? 0}
                      </td>
                      <td 
                        className={`${styles.tdBoldCenter} text-blue-600 cursor-pointer hover:underline`}
                        onClick={() => setSelectedDetail({ resource: row.validatedBy, resourceId: row._id, type: "Total", value: row.total })}
                      >
                        {row.total ?? 0}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className={styles.noData}>
                      No Records Found
                    </td>
                  </tr>
                )}
                {allRows.length > 0 && (
                    <tr className={styles.totalRow}>
                        <td className={styles.totalTd}>Total</td>
                        <td 
                            className={`${styles.totalTdCenter} text-blue-600 cursor-pointer hover:underline`}
                            onClick={() =>
                              setSelectedDetail({
                                resource: "All Resources",
                                type: "Casing A",
                                value: totals["Casing A"],
                                resourceId: selectedResourceFilters.length === 1 ? selectedResourceFilters[0].value : undefined,
                                resourceIds: selectedResourceFilters.length > 1 ? selectedResourceFilters.map((r) => r.value).filter(Boolean) : undefined,
                              })
                            }
                        >
                            {totals["Casing A"]}
                        </td>
                        <td 
                            className={`${styles.totalTdCenter} text-blue-600 cursor-pointer hover:underline`}
                            onClick={() =>
                              setSelectedDetail({
                                resource: "All Resources",
                                type: "Casing B",
                                value: totals["Casing B"],
                                resourceId: selectedResourceFilters.length === 1 ? selectedResourceFilters[0].value : undefined,
                                resourceIds: selectedResourceFilters.length > 1 ? selectedResourceFilters.map((r) => r.value).filter(Boolean) : undefined,
                              })
                            }
                        >
                            {totals["Casing B"]}
                        </td>
                        <td 
                            className={`${styles.totalTdCenter} text-blue-600 cursor-pointer hover:underline`}
                            onClick={() =>
                              setSelectedDetail({
                                resource: "All Resources",
                                type: "Casing C",
                                value: totals["Casing C"],
                                resourceId: selectedResourceFilters.length === 1 ? selectedResourceFilters[0].value : undefined,
                                resourceIds: selectedResourceFilters.length > 1 ? selectedResourceFilters.map((r) => r.value).filter(Boolean) : undefined,
                              })
                            }
                        >
                            {totals["Casing C"]}
                        </td>
                        <td 
                            className={`${styles.totalTdCenter} text-blue-600 cursor-pointer hover:underline`}
                            onClick={() =>
                              setSelectedDetail({
                                resource: "All Resources",
                                type: "Casing D",
                                value: totals["Casing D"],
                                resourceId: selectedResourceFilters.length === 1 ? selectedResourceFilters[0].value : undefined,
                                resourceIds: selectedResourceFilters.length > 1 ? selectedResourceFilters.map((r) => r.value).filter(Boolean) : undefined,
                              })
                            }
                        >
                            {totals["Casing D"]}
                        </td>
                        <td 
                            className={`${styles.totalTdCenter} text-blue-600 cursor-pointer hover:underline`}
                            onClick={() =>
                              setSelectedDetail({
                                resource: "All Resources",
                                type: "Scrap",
                                value: totals.scrap,
                                resourceId: selectedResourceFilters.length === 1 ? selectedResourceFilters[0].value : undefined,
                                resourceIds: selectedResourceFilters.length > 1 ? selectedResourceFilters.map((r) => r.value).filter(Boolean) : undefined,
                              })
                            }
                        >
                            {totals.scrap}
                        </td>
                        <td 
                            className={`${styles.totalTdBoldCenter} text-blue-600 cursor-pointer hover:underline`}
                            onClick={() =>
                              setSelectedDetail({
                                resource: "All Resources",
                                type: "Total",
                                value: totals.total,
                                resourceId: selectedResourceFilters.length === 1 ? selectedResourceFilters[0].value : undefined,
                                resourceIds: selectedResourceFilters.length > 1 ? selectedResourceFilters.map((r) => r.value).filter(Boolean) : undefined,
                              })
                            }
                        >
                            {totals.total}
                        </td>
                    </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>


      <ValidatedTyresModal 
        isOpen={!!selectedResource} 
        onClose={() => setSelectedResource(null)} 
        resourceName={selectedResource?.name} 
        resourceId={selectedResource?.id}
        dateFrom={dateFrom}
        dateTo={dateTo}
      />

      <CustomDetailModal 
        isOpen={!!selectedDetail} 
        onClose={() => setSelectedDetail(null)} 
        modalWidth="80vw"
        title={`Details for ${selectedDetail?.type} (${selectedDetail?.resource})`}
        filterComponent={
          <button
            type="button"
            disabled={modalLoading || downloadLoading || !selectedDetail}
            className="flex items-center gap-2 bg-[#22C55E] text-white px-5 py-2.5 rounded-xl hover:bg-green-600 transition-all shadow-sm hover:shadow-md font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadDetailData();
            }}
          >
            <BiCloudDownload size={20} className={downloadLoading ? "animate-pulse" : ""} />
            <span>{downloadLoading ? "Preparing…" : "Download Data"}</span>
          </button>
        }
        columns={VALIDATED_TYRES_RESOURCE_DETAIL_COLUMNS}
        loading={modalLoading}
        data={modalData.map((d, idx) => ({
          id: idx,
          zoneName: d.zoneName || "-",
          regionName: d.regionName || "-",
          hubName: d.hubName || "-",
          stencilNo: d.stencilNo || "-",
          tyreValidationDate: d.tyreValidationDate
            ? new Date(d.tyreValidationDate).toLocaleDateString("en-GB")
            : "-",
          casingType: resolveCasingTypeDisplay(d.casingType, isScrapDetail || isScrapTyreStatus(d.status ?? d.tyreStatus)),
          validatedBy: d.validatedBy || "-",
        }))}
      />
    </div>
  );
};

export default ValidatedTyresResourceTable;
